import {Icon} from '@wandb/components';
import React from 'react';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {trackCrossProjectPinnedRuns} from '../../../services/analytics/crossProjectRunsEvents';
import {useViewAction} from '../../../state/views/hooks';
import * as RunSetActions from '../../../state/views/runSet/actions';
import {isRunPinned} from '../../../state/views/runSet/utils';
import {useMaxPinnedRuns} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {type WBTableRowWithRunSetInfo} from '../../../util/tablecols';
import {useRunSelectorFeatureEnabled} from '../RunSelectorFeaturesContext';
import {
  useCurrentRunSetRef,
  usePinnedAndBaseline,
  useRunSelectorProjectInfo,
} from '../RunSelectorRunSetContext';
import {isCrossProjectRun, isRunBaseline} from '../util';
import {RowHeaderMenuOption} from './RowHeaderMenuOption';

type GetBaselineLabelOpts = {
  isAnyRunBaseline: boolean;
  isRunBaseline: boolean;
};

function getBaselineLabel({
  isAnyRunBaseline,
  isRunBaseline,
}: GetBaselineLabelOpts): string {
  if (isRunBaseline) {
    return 'Remove baseline';
  } else if (isAnyRunBaseline) {
    return 'Replace baseline';
  } else {
    return 'Set baseline';
  }
}

type GetPinInfoLabelOpts = {
  isRunBaseline: boolean;
  isPinned: boolean;
  pinnedRunCount: number;
  maxPinnedRuns: number;
};

function getPinInfoLabel({
  isRunBaseline,
  isPinned,
  pinnedRunCount,
  maxPinnedRuns,
}: GetPinInfoLabelOpts): string | null {
  if (pinnedRunCount >= maxPinnedRuns && !isPinned) {
    return `${maxPinnedRuns} max`;
  }
  if (!isPinned) {
    return `${pinnedRunCount} of ${maxPinnedRuns}`;
  }
  return null;
}

type RunPinningAndBaselineMenuOptions = {
  row: WBTableRowWithRunSetInfo;
};

function pinLimitWarning() {
  toast(
    <>
      <strong>Pin limit reached.</strong> Unpin a few runs for greater
      usability.
    </>
  );
}

export function RunPinningAndBaselineMenuOptions({
  row,
}: RunPinningAndBaselineMenuOptions) {
  const isEnabled = useRunSelectorFeatureEnabled();
  const runSetRef = useCurrentRunSetRef();
  const {pinnedRunIds, baselineRunId} = usePinnedAndBaseline();
  const maxPinnedRuns = useMaxPinnedRuns();
  const {entityName, projectName} = useRunSelectorProjectInfo();

  const pinned = isRunPinned(pinnedRunIds, row);
  const baseline = isRunBaseline(baselineRunId, row);
  const isAnyRunBaseline = Boolean(baselineRunId);
  const setPinnedRun = useViewAction(runSetRef, RunSetActions.setPinnedRun);
  const setBaselineRun = useViewAction(runSetRef, RunSetActions.setBaselineRun);

  const canPinRun = pinnedRunIds.length < maxPinnedRuns;
  const remainingPins = maxPinnedRuns - pinnedRunIds.length;

  const onPinClick = () => {
    window.analytics?.track(pinned ? 'Unpinned row' : 'Pinned row', {
      pinnedRunCount: pinnedRunIds.length,
    });
    if (
      pinned &&
      isCrossProjectRun(row.project, {entityName, name: projectName})
    ) {
      trackCrossProjectPinnedRuns({
        action: 'Unpin cross-project run',
      });
    }
    setPinnedRun(row, !pinned);

    if (!pinned && remainingPins === 1) {
      pinLimitWarning();
    }
  };
  const onBaselineClick = () => {
    window.analytics?.track(baseline ? 'Removed baseline' : 'Added baseline');
    if (isCrossProjectRun(row.project, {entityName, name: projectName})) {
      trackCrossProjectPinnedRuns({
        action: baseline
          ? 'Remove cross-project run as baseline'
          : 'Set cross-project run as baseline',
      });
    }
    setBaselineRun(baseline ? null : row);

    if (!baseline && remainingPins === 1) {
      pinLimitWarning();
    }
  };

  const baselineLabel = getBaselineLabel({
    isAnyRunBaseline,
    isRunBaseline: baseline,
  });

  const pinInfoLabel = getPinInfoLabel({
    isRunBaseline: baseline,
    isPinned: pinned,
    pinnedRunCount: pinnedRunIds.length,
    maxPinnedRuns,
  });

  return (
    <>
      {isEnabled('pinning') && !baseline && (
        <RowHeaderMenuOption
          icon={<Icon icon="pin" />}
          infoLabel={pinInfoLabel}
          label={pinned ? 'Unpin run' : 'Pin run'}
          onSelect={onPinClick}
          disabled={!canPinRun && !pinned}
        />
      )}
      {isEnabled('baselining') && (
        <RowHeaderMenuOption
          icon={<Icon icon="bookmark-my-projects" />}
          label={baselineLabel}
          onSelect={onBaselineClick}
          disabled={!canPinRun && !pinned}
        />
      )}
    </>
  );
}
